<template>
  <div class="product-card__ref product-card__ref--refco">
    <slider
      :number-of-children="images.length + 1"
      :number-on-screen="1"
      :gap="0"
      no-loading-state
      loop
    >
      <nuxt-link :to="to" class="product-card__ref__image">
        <lazy-image
          :image="imageMobile.image"
          :widths="imageMobile.widths"
          :sizes="imageMobile.sizes"
          class="d-block d-md-none"
          provider="sanity"
        />

        <lazy-image
          :image="imageDesktop.image"
          :widths="imageDesktop.widths"
          :sizes="imageDesktop.sizes"
          class="d-none d-md-block"
          provider="sanity"
        />
      </nuxt-link>

      <nuxt-link
        v-for="image of images"
        :key="image._key"
        :to="to"
        class="product-card__ref__image"
      >
        <lazy-image
          :image="image.imageMobile.image"
          :widths="image.imageMobile.widths"
          :sizes="image.imageMobile.sizes"
          class="d-block d-md-none"
          provider="sanity"
        />

        <lazy-image
          :image="image.imageDesktop.image"
          :widths="image.imageDesktop.widths"
          :sizes="image.imageDesktop.sizes"
          class="d-none d-md-block"
          provider="sanity"
        />
      </nuxt-link>
    </slider>

    <nuxt-link :to="to" class="product-card__ref__content">
      <div class="inner d-md-flex align-center">
        <div
          v-if="!hideTags && color.color_status === 'new'"
          class="tag tag--yellow-and-secondary"
        >
          <icon name="new" />
          <span>{{ $t('product.tag.new_color') }}</span>
        </div>

        <div
          v-if="!hideTags && color.color_status === 'leaving_soon'"
          class="tag tag--sun-and-white"
        >
          <icon name="estimated-time" />
          <span>{{ $t('product.tag.leaving_soon') }}</span>
        </div>

        <div>
          <div>
            <p class="product-card__ref__name mb-0">
              {{ color.label }}

              <!-- 
                TODO : LINKED PRODUCT
                <span v-if="color.linkedVariantName">
                  / {{ color.linkedVariantName }}
                </span>
                <span v-else-if="color.third_variant_name">
                  / {{ color.third_variant_name }}
                </span> -->

              <span class="product-card__ref__icon ml-0-6 mb-0-2 d-md-none">
                <icon name="arrow-right" />
              </span>
            </p>
          </div>

          <div
            class="product-card__prices d-flex flex-wrap align-center pt-0-2 pt-md-0"
          >
            <p
              v-if="price > 0"
              class="price d-flex align-baseline mb-0 mr-0-6 mr-md-1"
            >
              <span class="number text-secondary pr-0-4"
                >{{ price }}&euro;</span
              >
              <span
                class="text-label text-md-extra-small text-uppercase text-no-wrap"
                >{{ $t('product.in_preco') }}</span
              >
            </p>

            <p
              v-if="compareAtPrice > 0"
              class="price compare d-flex align-baseline mb-0"
            >
              <span class="number text-secondary pr-0-4"
                >{{ compareAtPrice }}&euro;</span
              >
              <span
                class="text-label text-md-extra-small text-uppercase text-no-wrap"
                >{{ $t('product.in_stock') }}</span
              >
            </p>
          </div>
        </div>
        <div class="product-card__ref__icon d-none d-md-block">
          <icon name="arrow-right" />
        </div>
      </div>
    </nuxt-link>
  </div>
</template>

<script>
import LazyImage from '@/components/Image/LazyImage'
import Icon from '@/components/icons/Icon'
import Slider from '@/components/slider/Slider'

import { PRODUCT_IMAGE_FORMAT } from '@/const/image'

import { getSanityBunnyImageLink } from '@/utils/image'

export default {
  name: 'ProductRefCoChild',
  components: {
    LazyImage,
    Icon,
    Slider,
  },
  props: {
    color: {
      type: Object,
      default: () => ({}),
    },
    to: {
      type: [String, Object],
      default: '',
    },
    type: {
      type: String,
      default: 'list',
    },
    typeMobile: {
      type: String,
      default: 'list',
    },
    price: {
      type: Number,
      default: 0,
    },
    compareAtPrice: {
      type: Number,
      default: 0,
    },
    hideTags: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    imageMobile() {
      if (this.typeMobile === 'full') {
        return {
          image: this.getImage(this.color.image, 'MOBILE'),
          widths: [390, 780, 1170],
          sizes: '100vw',
        }
      }
      return {
        image: this.getImage(this.color.image, 'LIST_MOBILE'),
        widths: [195, 390, 585],
        sizes: '50vw',
      }
    },
    imageDesktop() {
      if (this.type === 'full') {
        return {
          image: this.getImage(this.color.image, 'HIGHLIGHT'),
          widths: [1600, 2400, 3200],
          sizes: '100vw',
        }
      }
      if (this.type === 'half') {
        return {
          image: this.getImage(this.color.image, 'HALF'),
          widths: [720, 1440, 2160],
          sizes: '50vw',
        }
      }
      return {
        image: this.getImage(this.color.image, 'LIST'),
        widths: [480, 960, 1440],
        sizes: '33vw',
      }
    },
    images() {
      if (
        this.color.slider?.elements &&
        this.color.slider.elements.length > 0
      ) {
        return this.color.slider.elements
          .filter((element) => element._type === 'slider_image')
          .slice(1, -1)
          .map((child) => {
            if (this.type === 'full') {
              return {
                _key: child._key,
                imageDesktop: {
                  image: this.getImage(child.image, 'HIGHLIGHT'),
                  widths: [1600, 2400, 3200],
                  sizes: '100vw',
                },
                imageMobile: {
                  image: this.getImage(child.image, 'MOBILE'),
                  widths: [390, 780, 1170],
                  sizes: '100vw',
                },
              }
            }

            return {
              _key: child._key,
              imageDesktop:
                this.type === 'half'
                  ? {
                      image: this.getImage(child.image, 'HALF'),
                      widths: [720, 1440, 2160],
                      sizes: '50vw',
                    }
                  : {
                      image: this.getImage(child.image, 'LIST'),
                      widths: [480, 960, 1440],
                      sizes: '33vw',
                    },
              imageMobile: {
                image: this.getImage(child.image, 'LIST_MOBILE'),
                widths: [195, 390, 585],
                sizes: '50vw',
              },
            }
          })
      }

      return []
    },
  },
  methods: {
    getImage(image, type) {
      if (image) {
        return {
          url: getSanityBunnyImageLink(image, PRODUCT_IMAGE_FORMAT[type]),
          dimensions: PRODUCT_IMAGE_FORMAT[type],
        }
      }

      console.warn('No image found for color', color._key)

      return null
    },
  },
}
</script>

<style lang="scss">
.product-card__ref {
  .slider__controls {
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.2s var(--asphalte-animation-function);
  }

  @include mq($from: tablet) {
    @include on-hover-and-focus {
      .slider__controls {
        pointer-events: all;
        opacity: 1;
      }
    }
  }
}
</style>
